import Client from "./_client";

/**
 * Onboard a Case Master free-trial user.
 *
 * This is a direct call from the frontend: the backend creates the user and
 * emails them their login credentials (the password is never returned in the
 * response).
 *
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @param {string} params.contact contact number; sent as `phone`, the field
 *   name the endpoint's validation schema expects
 * @param {('free'|'pro'|'premium')} [params.plan='free'] accepted but ignored —
 *   this endpoint always provisions the free plan
 * @param {Object} [params.metadata] extra profile details stored on the user's
 *   metadata: { whatsappNumber, collegeName, academicYear, aspiringRole, referralSource }
 * @returns {Promise<Object>} the `data` payload from a successful response
 * @throws {Error & { status?: number }} on failure. `status === 409` means an
 *   account already exists for this email and the credential email has been
 *   re-sent; any other status is a genuine validation/server failure.
 */
export const subscribeCaseMasterUser = async ({ name, email, contact, plan = "free", metadata }) => {
  // MUST stay on the Case Master endpoint, not the generic `/user/b2c/free-trial`
  // one: only this route sets `metadata.casemasterPlanId` (and files the account
  // under the Case Master client/batch). Without that key the interview portal
  // reads the account as non-B2C and unlocks every case.
  const result = await new Client({
    path: `/user/b2c/case-master/free-trial`,
    payload: { name, email, phone: contact, plan, metadata },
  }).post();

  if (!(result.ok || result.code === 200)) {
    const error = new Error(
      result.message || "Uh Oh! Something went wrong, please try again."
    );
    // 409 means the email already has an account; the backend has re-sent the
    // credentials, so callers route the user to login rather than to the form.
    error.status = result.status;
    throw error;
  }

  return result?.data;
};
